// Language of the auth-hosted pages people reach from an email: the
// invitation (/invite/<token>), forgot password and reset password.
//
// The ERP remembers a user's language in ITS origin's localStorage
// (``vivid360_language``), which this origin cannot read, so the links it
// has us email carry it as ``?lang=ar``. The choice is then remembered
// here under the same key, so going on to "Forgot password?" or back to
// sign in keeps it. English is the default; Arabic is right-to-left.
import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

export const LANG_KEY = 'vivid360_language';
export const SUPPORTED_LANGS = ['en', 'ar'];

export function normaliseLang(value) {
  if (typeof value !== 'string') return null;
  const code = value.trim().toLowerCase().replace('_', '-').split('-')[0];
  return SUPPORTED_LANGS.includes(code) ? code : null;
}

function readStored() {
  try { return normaliseLang(window.localStorage.getItem(LANG_KEY)); } catch { return null; }
}

function remember(lang) {
  try { window.localStorage.setItem(LANG_KEY, lang); } catch { /* private mode: not remembered */ }
}

const EN = {
  // Shared chrome (also on the sign-in page's footer and side panel).
  'footer.privacy': 'Privacy',
  'decor.title': 'One platform to run your entire business.',
  'decor.body': 'ERP, automation, and analytics, all in one platform. Sign in to continue, or create a workspace in under a minute.',
  'lang.switch': 'العربية',
  'lang.switchLabel': 'اعرض الصفحة بالعربية',
  'common.email': 'Email',
  'common.password': 'Password',
  'common.showPassword': 'Show password',
  'common.hidePassword': 'Hide password',
  'common.backToSignIn': '← Back to sign in',
  'common.goToSignIn': 'Go to sign in',
  'common.signIn': 'Sign in',
  'common.docTitle': 'Vivid360, Sign in',
  'common.minLength': 'Use at least {n} characters',
  'common.atLeast': 'At least {n} characters.',
  'common.mismatch': 'The two passwords do not match',
  'common.tooMany': 'Too many attempts. Wait a while, then try again.',
  'common.tryAgainIn': 'Too many attempts. Try again in {mins} minutes.',

  // Sign in
  'login.title': 'Welcome back',
  'login.subtitle': 'Sign in to your Vivid360 workspace.',
  'login.notice.expired': 'Your session ended. Sign in to carry on.',
  'login.notice.signedOut': 'You are signed out.',
  'login.notice.forbidden': 'You do not have access to that workspace. Sign in with another account.',
  'login.notice.returnTo': 'We will take you back to {label}.',
  'login.newToVivid': 'New to Vivid360?',
  'login.createAccount': 'Create account',
  'login.continueGoogle': 'Continue with Google',
  'login.continueMicrosoft': 'Continue with Microsoft',
  'login.continueSso': 'Continue with SSO',
  'login.orContinueWith': 'Or continue with',
  'login.emailPlaceholder': 'my@workemail.com',
  'login.emailHelper': 'Please use your work email address.',
  'login.emailRequired': 'Email is required',
  'login.emailInvalid': 'Enter a valid email address',
  'login.passwordRequired': 'Password is required',
  'login.rememberMe': 'Remember me',
  'login.signingIn': 'Signing in…',
  'login.doNotHaveAccount': 'Do not have an account?',

  // Invitation
  'invite.opening': 'Opening your invitation…',
  'invite.notFound.title': 'This invitation link is not valid',
  'invite.notFound.body': 'The link is incomplete or was replaced by a newer invitation. Use the most recent email, or ask for a new invitation.',
  'invite.expired.title': 'This invitation has expired',
  'invite.expired.body': 'Invitations work for 72 hours. Ask the person who invited you to send it again.',
  'invite.revoked.title': 'This invitation was withdrawn',
  'invite.revoked.body': 'The company withdrew this invitation. Ask them for a new one if you still need access.',
  'invite.used.title': 'This invitation was already used',
  'invite.used.body': 'You have already joined with this link. Sign in to continue.',
  'invite.openFailed.title': 'We could not open this invitation',
  'invite.openFailed.busy': 'Too many attempts. Wait a minute, then reload the page.',
  'invite.openFailed.body': 'Please try again in a moment.',
  'invite.join': 'Join {org}',
  'invite.nCompanies': '{n} companies',
  'invite.byInviter': '{inviter} invited you to join {org} on Vivid360.',
  'invite.neutral': "You've been invited to join {org} on Vivid360.",
  'invite.byInviterMany': '{inviter} invited you to join these companies on Vivid360:',
  'invite.neutralMany': "You've been invited to join these companies on Vivid360:",
  'invite.yourRole': 'Your role: {roles}',
  'invite.helperExists': 'You already have a Vivid360 account with this email.',
  'invite.helperNew': 'This invitation is for this address.',
  'invite.password': 'Password',
  'invite.forgot': 'Forgot password?',
  'invite.firstName': 'First name',
  'invite.lastName': 'Last name',
  'invite.choosePassword': 'Choose a password',
  'invite.confirmPassword': 'Confirm password',
  'invite.joining': 'Joining…',
  'invite.notYou': 'Not you? Ignore the email; nothing happens until someone accepts.',
  'invite.err.password': 'Enter your password',
  'invite.err.firstName': 'Enter your first name',
  'invite.err.lastName': 'Enter your last name',
  'invite.err.choosePassword': 'Choose a password',
  'invite.err.wrongPassword': 'That is not the password of this account',
  'invite.err.failed': 'Could not accept the invitation. Please try again.',
  'invite.err.tooLong': 'Use at most 100 characters',

  // Forgot password
  'forgot.title': 'Forgot your password?',
  'forgot.intro': "Enter the email you sign in with and we'll send you a link to choose a new one.",
  'forgot.invalidEmail': 'Enter the email address you sign in with',
  'forgot.tooMany': 'Too many reset requests. Wait a while, then try again.',
  'forgot.send': 'Send reset link',
  'forgot.sending': 'Sending…',
  'forgot.checkEmail': 'Check your email',
  'forgot.neutral': "If that address has an account, we've sent it a link to reset the password. The link works for 60 minutes.",
  'forgot.nothingArrived': 'Nothing after a few minutes? Check your spam folder. If it still has not arrived, email may not be set up yet: ask your administrator to reset your password for you.',

  // Reset password
  'reset.title': 'Choose a new password',
  'reset.newPassword': 'New password',
  'reset.confirm': 'Confirm new password',
  'reset.save': 'Change password',
  'reset.saving': 'Saving…',
  'reset.err.choose': 'Choose a new password',
  'reset.err.failed': 'Could not change the password. Please try again.',
  'reset.done.title': 'Password changed',
  'reset.done.body': 'Your password has been changed and you have been signed out everywhere. Sign in with the new one.',
  'reset.invalid.title': 'This link no longer works',
  'reset.invalid.body': 'The reset link has expired, was already used, or is incomplete. Reset links work once, for 60 minutes.',
  'reset.invalid.newLink': 'Send a new link',
};

const AR = {
  'footer.privacy': 'الخصوصية',
  'decor.title': 'منصة واحدة لإدارة أعمالك بالكامل.',
  'decor.body': 'تخطيط الموارد والأتمتة والتحليلات في مكان واحد. سجّل الدخول للمتابعة، أو أنشئ مساحة عمل في أقل من دقيقة.',
  'lang.switch': 'English',
  'lang.switchLabel': 'Show this page in English',
  'common.email': 'البريد الإلكتروني',
  'common.password': 'كلمة المرور',
  'common.showPassword': 'إظهار كلمة المرور',
  'common.hidePassword': 'إخفاء كلمة المرور',
  'common.backToSignIn': '→ العودة إلى تسجيل الدخول',
  'common.goToSignIn': 'الانتقال إلى تسجيل الدخول',
  'common.signIn': 'تسجيل الدخول',
  'common.docTitle': 'Vivid360، تسجيل الدخول',
  'common.minLength': 'استخدم {n} أحرف على الأقل',
  'common.atLeast': '{n} أحرف على الأقل.',
  'common.mismatch': 'كلمتا المرور غير متطابقتين',
  'common.tooMany': 'محاولات كثيرة جدًا. انتظر قليلًا ثم حاول مرة أخرى.',
  'common.tryAgainIn': 'محاولات كثيرة جدًا. حاول مرة أخرى بعد {mins} دقيقة.',

  'login.title': 'مرحبًا بعودتك',
  'login.subtitle': 'سجّل الدخول إلى مساحة عمل Vivid360.',
  'login.notice.expired': 'انتهت جلستك. سجّل الدخول للمتابعة.',
  'login.notice.signedOut': 'تم تسجيل خروجك.',
  'login.notice.forbidden': 'لا تملك صلاحية الوصول إلى مساحة العمل هذه. سجّل الدخول بحساب آخر.',
  'login.notice.returnTo': 'سنعيدك إلى {label}.',
  'login.newToVivid': 'جديد على Vivid360؟',
  'login.createAccount': 'إنشاء حساب',
  'login.continueGoogle': 'المتابعة باستخدام Google',
  'login.continueMicrosoft': 'المتابعة باستخدام Microsoft',
  'login.continueSso': 'المتابعة عبر SSO',
  'login.orContinueWith': 'أو تابع باستخدام',
  'login.emailPlaceholder': 'my@workemail.com',
  'login.emailHelper': 'يرجى استخدام عنوان بريدك الإلكتروني الخاص بالعمل.',
  'login.emailRequired': 'البريد الإلكتروني مطلوب',
  'login.emailInvalid': 'أدخل عنوان بريد إلكتروني صالحًا',
  'login.passwordRequired': 'كلمة المرور مطلوبة',
  'login.rememberMe': 'تذكّرني',
  'login.signingIn': 'جارٍ تسجيل الدخول…',
  'login.doNotHaveAccount': 'ليس لديك حساب؟',

  'invite.opening': 'جارٍ فتح دعوتك…',
  'invite.notFound.title': 'رابط الدعوة هذا غير صالح',
  'invite.notFound.body': 'الرابط غير مكتمل أو استُبدل بدعوة أحدث. استخدم أحدث رسالة بريد إلكتروني، أو اطلب دعوة جديدة.',
  'invite.expired.title': 'انتهت صلاحية هذه الدعوة',
  'invite.expired.body': 'تبقى الدعوات صالحة لمدة 72 ساعة. اطلب من الشخص الذي دعاك أن يرسلها مرة أخرى.',
  'invite.revoked.title': 'تم سحب هذه الدعوة',
  'invite.revoked.body': 'سحبت الشركة هذه الدعوة. اطلب منهم دعوة جديدة إذا كنت لا تزال بحاجة إلى الوصول.',
  'invite.used.title': 'تم استخدام هذه الدعوة من قبل',
  'invite.used.body': 'لقد انضممت بالفعل باستخدام هذا الرابط. سجّل الدخول للمتابعة.',
  'invite.openFailed.title': 'تعذّر فتح هذه الدعوة',
  'invite.openFailed.busy': 'محاولات كثيرة جدًا. انتظر دقيقة ثم أعد تحميل الصفحة.',
  'invite.openFailed.body': 'يرجى المحاولة مرة أخرى بعد قليل.',
  'invite.join': 'انضم إلى {org}',
  'invite.nCompanies': '{n} شركات',
  'invite.byInviter': 'دعاك {inviter} للانضمام إلى {org} على Vivid360.',
  'invite.neutral': 'تمت دعوتك للانضمام إلى {org} على Vivid360.',
  'invite.byInviterMany': 'دعاك {inviter} للانضمام إلى هذه الشركات على Vivid360:',
  'invite.neutralMany': 'تمت دعوتك للانضمام إلى هذه الشركات على Vivid360:',
  'invite.yourRole': 'دورك: {roles}',
  'invite.helperExists': 'لديك بالفعل حساب Vivid360 بهذا البريد الإلكتروني.',
  'invite.helperNew': 'هذه الدعوة لهذا العنوان.',
  'invite.password': 'كلمة المرور',
  'invite.forgot': 'نسيت كلمة المرور؟',
  'invite.firstName': 'الاسم الأول',
  'invite.lastName': 'اسم العائلة',
  'invite.choosePassword': 'اختر كلمة مرور',
  'invite.confirmPassword': 'تأكيد كلمة المرور',
  'invite.joining': 'جارٍ الانضمام…',
  'invite.notYou': 'لست أنت؟ تجاهل الرسالة؛ لن يحدث شيء ما لم يقبلها أحد.',
  'invite.err.password': 'أدخل كلمة المرور',
  'invite.err.firstName': 'أدخل اسمك الأول',
  'invite.err.lastName': 'أدخل اسم العائلة',
  'invite.err.choosePassword': 'اختر كلمة مرور',
  'invite.err.wrongPassword': 'هذه ليست كلمة مرور هذا الحساب',
  'invite.err.failed': 'تعذّر قبول الدعوة. يرجى المحاولة مرة أخرى.',
  'invite.err.tooLong': 'استخدم 100 حرف كحد أقصى',

  'forgot.title': 'هل نسيت كلمة المرور؟',
  'forgot.intro': 'أدخل البريد الإلكتروني الذي تسجّل الدخول به وسنرسل إليك رابطًا لاختيار كلمة مرور جديدة.',
  'forgot.invalidEmail': 'أدخل عنوان البريد الإلكتروني الذي تسجّل الدخول به',
  'forgot.tooMany': 'طلبات إعادة تعيين كثيرة جدًا. انتظر قليلًا ثم حاول مرة أخرى.',
  'forgot.send': 'إرسال رابط إعادة التعيين',
  'forgot.sending': 'جارٍ الإرسال…',
  'forgot.checkEmail': 'تحقّق من بريدك الإلكتروني',
  'forgot.neutral': 'إذا كان لهذا العنوان حساب، فقد أرسلنا إليه رابطًا لإعادة تعيين كلمة المرور. يبقى الرابط صالحًا لمدة 60 دقيقة.',
  'forgot.nothingArrived': 'لم يصل شيء بعد بضع دقائق؟ تحقّق من مجلد الرسائل غير المرغوب فيها. إذا لم يصل بعد، فربما لم يُعدّ البريد الإلكتروني بعد: اطلب من المسؤول إعادة تعيين كلمة المرور لك.',

  'reset.title': 'اختر كلمة مرور جديدة',
  'reset.newPassword': 'كلمة المرور الجديدة',
  'reset.confirm': 'تأكيد كلمة المرور الجديدة',
  'reset.save': 'تغيير كلمة المرور',
  'reset.saving': 'جارٍ الحفظ…',
  'reset.err.choose': 'اختر كلمة مرور جديدة',
  'reset.err.failed': 'تعذّر تغيير كلمة المرور. يرجى المحاولة مرة أخرى.',
  'reset.done.title': 'تم تغيير كلمة المرور',
  'reset.done.body': 'تم تغيير كلمة المرور وتسجيل خروجك من جميع الأجهزة. سجّل الدخول بكلمة المرور الجديدة.',
  'reset.invalid.title': 'هذا الرابط لم يعد صالحًا',
  'reset.invalid.body': 'انتهت صلاحية رابط إعادة التعيين، أو استُخدم من قبل، أو أنه غير مكتمل. تعمل روابط إعادة التعيين مرة واحدة لمدة 60 دقيقة.',
  'reset.invalid.newLink': 'إرسال رابط جديد',
};

export const DICTIONARIES = { en: EN, ar: AR };

function lookup(lang, key) {
  return (DICTIONARIES[lang] || EN)[key] ?? EN[key] ?? key;
}

/** ``"Join {org}"`` + ``{org: 'Acme'}`` -> ``"Join Acme"``. */
export function translate(lang, key, vars = {}) {
  return lookup(lang, key).replace(/\{(\w+)\}/g, (m, name) => (name in vars ? String(vars[name]) : m));
}

/** Like ``translate`` but a variable may be a React node (bold names). */
export function translateNodes(lang, key, vars = {}) {
  const parts = lookup(lang, key).split(/(\{\w+\})/);
  return parts.map((part, i) => {
    const m = /^\{(\w+)\}$/.exec(part);
    if (m && m[1] in vars) return <span key={i}>{vars[m[1]]}</span>;
    return part;
  });
}

/**
 * The page's language: ``?lang=`` first (and remembered), then the one
 * remembered here, else English. Sets ``<html lang dir>`` while the page
 * is shown and puts them back when it goes (the sign-in page is not
 * translated yet).
 */
export function useLang() {
  const { search } = useLocation();
  const fromUrl = normaliseLang(new URLSearchParams(search).get('lang'));
  const [lang, setLangState] = useState(() => fromUrl || readStored() || 'en');

  useEffect(() => {
    if (fromUrl) {
      remember(fromUrl);
      setLangState(fromUrl);
    }
  }, [fromUrl]);

  const dir = lang === 'ar' ? 'rtl' : 'ltr';
  useEffect(() => {
    const html = document.documentElement;
    const before = { lang: html.lang, dir: html.dir };
    const beforeTitle = document.title;
    html.lang = lang;
    html.dir = dir;
    document.title = translate(lang, 'common.docTitle');
    return () => {
      html.lang = before.lang || 'en';
      html.dir = before.dir || 'ltr';
      document.title = beforeTitle;
    };
  }, [lang, dir]);

  const setLang = useCallback((next) => {
    const code = normaliseLang(next) || 'en';
    remember(code);
    setLangState(code);
  }, []);
  const t = useCallback((key, vars) => translate(lang, key, vars), [lang]);
  const tn = useCallback((key, vars) => translateNodes(lang, key, vars), [lang]);
  return { lang, dir, t, tn, setLang };
}

/** "العربية" / "English" toggle for the page header. */
export function LangSwitch({ lang, setLang }) {
  const other = lang === 'ar' ? 'en' : 'ar';
  return (
    <button
      type="button"
      onClick={() => setLang(other)}
      lang={other}
      aria-label={translate(lang, 'lang.switchLabel')}
      data-testid="lang-switch"
      className="text-[13px] font-medium text-stone-500 hover:text-ink"
    >
      {translate(lang, 'lang.switch')}
    </button>
  );
}
